// =============================================================================
// FE-18 — Delegation Loading Skeletons (shared)
// Consistent skeleton components used across all delegation pages and
// components so loading states feel unified throughout the feature.
// =============================================================================

import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

// ---------------------------------------------------------------------------
// DelegationCardSkeleton
// Mimics a single delegation card (MyDelegationsPage list item)
// ---------------------------------------------------------------------------

export function DelegationCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Skeleton className="h-9 w-9 rounded-full shrink-0" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-6 w-20 rounded-full shrink-0" />
        </div>
        <div className="space-y-1.5 pl-12">
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-3 w-40" />
        </div>
        <div className="pl-12 pt-1">
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DelegationListSkeleton
// Multiple cards — used when loading the full My Delegations list
// ---------------------------------------------------------------------------

interface DelegationListSkeletonProps {
  count?: number;
}

export function DelegationListSkeleton({ count = 3 }: DelegationListSkeletonProps) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <DelegationCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DelegationPageSkeleton
// Full-page skeleton — page header + list. Used for initial page load.
// ---------------------------------------------------------------------------

export function DelegationPageSkeleton() {
  return (
    <div className="space-y-6 pb-6">
      {/* Page header area */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-9 w-36 rounded-md" />
      </div>

      {/* Section header */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-5 w-5 rounded-full" />
      </div>

      {/* Cards */}
      <DelegationListSkeleton count={2} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DelegationSettingsSkeleton
// Settings form skeleton — used by DelegationSettingsPage (fallback import)
// ---------------------------------------------------------------------------

export function DelegationSettingsSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64 mt-1" />
          </CardHeader>
          <CardContent className="space-y-5">
            {[1, 2, 3].map((j) => (
              <div key={j} className="flex items-center justify-between">
                <div className="space-y-1.5 flex-1 mr-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                </div>
                <Skeleton className="h-5 w-9 rounded-full shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DelegationAuditSkeleton
// Table skeleton — used by DelegationAuditPage
// ---------------------------------------------------------------------------

export function DelegationAuditSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="h-3 w-56" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DelegationBannerSkeleton
// Slim skeleton for banner components that load async data (FE-12, FE-13)
// ---------------------------------------------------------------------------

interface DelegationBannerSkeletonProps {
  className?: string;
}

export function DelegationBannerSkeleton({ className }: DelegationBannerSkeletonProps) {
  return (
    <Skeleton className={`h-14 w-full rounded-lg ${className ?? ''}`} />
  );
}

// ---------------------------------------------------------------------------
// DelegationKpiSkeleton
// 4-card KPI row skeleton — used by DelegationAuditPage header
// ---------------------------------------------------------------------------

export function DelegationKpiSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardContent className="flex items-center gap-4 p-5">
            <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-10" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
